import { trackMenuNavigation } from '../menuAnalytics';
import * as translator from '../../../../utils/translator';

interface AllCategoriesSectionProps {
  exploreItems: ExploreItem[];
  sidebarTopCategoriesV2: SidebarTopCategory[];
  categoriesByRoot: any[];
  onCategoryClick: (category: SidebarTopCategory) => void;
  language: string;
  country: string;
  shouldShowOTC: boolean;
}

export function AllCategoriesSection({
  exploreItems,
  sidebarTopCategoriesV2,
  categoriesByRoot,
  onCategoryClick,
  language,
  country,
  shouldShowOTC
}: AllCategoriesSectionProps) {

  // Build a submenu category from a root catalog's children, so a root catalog
  // opens a sub menu listing its children (like the master branch) instead of
  // navigating directly to its catalog page.
  const buildCategoryFromRoot = (root: any): SidebarTopCategory => ({
    label: root.title,
    url: root.url,
    order: root.order || 0,
    mention: root.mention || undefined,
    mixpanel_title: root.mixpanel_title || undefined,
    isRootCatalog: true,
    items: (root.children || []).map((child: any) => {
      let sticker: string | null = null;
      try {
        sticker = child.settings ? JSON.parse(child.settings).categories_menu_sticker || null : null;
      } catch { /* ignore malformed settings */ }
      return {
        label: child.title,
        url: child.uri,
        template: 'single',
        visibility: 'all',
        sticker,
        mention: child.mention || undefined,
        mixpanel_title: child.mixpanel_title || null,
      };
    }),
  });

  const handleExploreItemClick = (item: ExploreItem) => {
    // Prefer a curated V2 submenu when one exists for this catalog.
    const matchingCategory = sidebarTopCategoriesV2.find(
      cat => cat.mention === item.mention || cat.label === item.label
    );
    if (matchingCategory) {
      onCategoryClick(matchingCategory);
      return;
    }

    // Otherwise, open a sub menu built from the root catalog's children.
    const matchingRoot = categoriesByRoot.find(
      root => (item.mention && root.mention === item.mention) || root.title === item.label
    );
    if (matchingRoot && matchingRoot.children && matchingRoot.children.length > 0) {
      onCategoryClick(buildCategoryFromRoot(matchingRoot));
      return;
    }

    if (item.url && item.url !== '#') {
      trackMenuNavigation({
        category: item.mixpanel_title || item.label,
        button_label: item.label,
        carousel: 'no',
        sub_category: '',
        link: item.url,
      });
      window.location.href = item.url;
    }
  };

  const handleExploreItemLinkClick = (item: ExploreItem, e: React.MouseEvent) => {
    e.preventDefault();
    trackMenuNavigation({
      category: item.mixpanel_title || item.label,
      button_label: item.label,
      carousel: 'no',
      sub_category: '',
      link: item.url,
    });
    if (item.url) {
      window.location.href = item.url;
    }
  };

  const handleOtcCategoryClick = (category: SidebarTopCategory) => {
    onCategoryClick(category);
  };

  const otcCategory = sidebarTopCategoriesV2.find(cat => cat.isOtc);

  return (
    <div id="all-categories-section" className="mt-4 bg-[#f2f4f6] rounded-[20px]">
      <span className="font-poppins font-medium block text-[#757575] tracking-[1px] uppercase px-5 leading-[26px] mb-2 md:px-6 md:text-[15px] pt-3 text-[13px]">
        {translator.translate(language, 'layout', 'menu/explore')}
      </span>
      <div className="flex flex-col">
        {exploreItems.map((item) => {
          const withNewTag = item.withNewTag || item.isNew;

          if (item.hasSubmenu) {
            return (
              <button
                key={item.key || item.label}
                id={`rx-explore-item-btn-${item.key}`}
                className="flex items-center w-full cursor-pointer text-left border-none bg-transparent justify-between h-[3.375rem] md:px-6 px-5 hover:bg-[#eaecef]"
                style={{ transition: 'background-color 0.2s ease' }}
                onClick={() => handleExploreItemClick(item)}
                type="button"
                data-has-submenu="1"
                data-key={item.key}
              >
                <div className="flex items-center gap-2 flex-1">
                  <span className="font-poppins font-medium text-[#1f2937] leading-[21px] text-[16px] tracking-normal">{item.label}</span>
                  {item.isNew && item.sticker && (
                    <span className="text-xs bg-primary-500 text-surface-card px-2 py-1 rounded-full font-medium">{item.sticker}</span>
                  )}
                  {withNewTag && !item.sticker && (
                    <span className="text-white linear-background-color-primary font-semibold text-xs ml-[0.5em] px-2 py-1 rounded-[4px]">
                      {translator.translate(language, 'layout', 'sidebar/new_tag')}
                    </span>
                  )}
                </div>
                <img src="/img_v4/icons/chevron-right-icon.svg" alt="" aria-hidden="true" className="h-[9.33px] w-[5.33px]" style={{ color: '#98a0af' }} />
              </button>
            );
          }

          return (
            <a
              key={item.key || item.label}
              id={`rx-explore-link-${item.key}`}
              href={item.url}
              onClick={(e) => handleExploreItemLinkClick(item, e)}
              className="flex items-center w-full cursor-pointer text-left border-none bg-transparent justify-between h-[3.375rem] md:px-6 px-5 hover:bg-[#eaecef]"
              style={{ transition: 'background-color 0.2s ease', textDecoration: 'none' }}
              data-category={item.mixpanel_title || item.label}
              data-button-label={item.label}
            >
              <div className="flex items-center gap-2 flex-1">
                <span className="font-poppins font-medium text-[#1f2937] leading-[21px] text-[16px] tracking-normal">{item.label}</span>
                {item.isNew && item.sticker && (
                  <span className="text-xs bg-primary-500 text-surface-card px-2 py-1 rounded-full font-medium">{item.sticker}</span>
                )}
                {withNewTag && !item.sticker && (
                  <span className="text-white linear-background-color-primary font-semibold text-xs ml-[0.5em] px-2 py-1 rounded-[4px]">
                    {translator.translate(language, 'layout', 'sidebar/new_tag')}
                  </span>
                )}
              </div>
              <img src="/img_v4/icons/chevron-right-icon.svg" alt="" aria-hidden="true" className="h-[9.33px] w-[5.33px]" style={{ color: '#98a0af' }} />
            </a>
          );
        })}

        {shouldShowOTC && otcCategory && (
          <button
            id={`rx-explore-item-btn-${otcCategory.mention}`}
            className="flex items-center w-full cursor-pointer text-left border-none bg-transparent justify-between h-[3.375rem] md:px-6 px-5 hover:bg-[#eaecef]"
            style={{ transition: 'background-color 0.2s ease' }}
            onClick={() => handleOtcCategoryClick(otcCategory)}
            type="button"
            data-has-submenu="1"
            data-key={`top-cat-detail-${otcCategory.mention}`}
            data-category={otcCategory.mixpanel_title || otcCategory.label}
          >
            <div className="flex items-center gap-2 flex-1">
              <span className="font-poppins font-medium text-[#1f2937] leading-[21px] text-[16px] tracking-normal">{otcCategory.label}</span>
              {(otcCategory.withNewTag || otcCategory.withNewTagFromMenu) && (
                <span className="text-white linear-background-color-primary font-semibold text-xs ml-[0.5em] px-2 py-1 rounded-[4px]">
                  {translator.translate(language, 'layout', 'sidebar/new_tag')}
                </span>
              )}
            </div>
            <img src="/img_v4/icons/chevron-right-icon.svg" alt="" aria-hidden="true" className="h-[9.33px] w-[5.33px]" style={{ color: '#98a0af' }} />
          </button>
        )}
      </div>
    </div>
  );
}
